import React, { Fragment, useState, useMemo } from 'react';
import Link from 'shared/SlashLink';
import usePageSeo from '../../hooks/usePageSeo';
import JsonLd, { webAppSchema } from 'shared/JsonLd';
import Header from '../../components/header/Header';
import PageTitle from '../../components/pagetitle/PageTitle';
import Scrollbar from '../../components/scrollbar/scrollbar';
import Footer from '../../components/footer/Footer';
import CtaSection from '../../components/CtaSection/CtaSection';
import ToolLeadCapture from '../../components/ToolLeadCapture/ToolLeadCapture';

// Ballpark monthly cost model (USD). On-prem amortizes capex + power/space +
// in-house labor; cloud is opex with managed services. Planning-level only.
const ONPREM = { perServer: 250, perTB: 30, perUser: 35, overhead: 500 };
const CLOUD = { perServer: 180, perTB: 45, perUser: 28, overhead: 300 };
const MIGRATION_PER_SERVER = 1200; // one-time migration project cost per server (ballpark)

const clampInt = (val, min, max) => {
    const n = parseInt(val, 10);
    if (Number.isNaN(n)) return min;
    return Math.min(max, Math.max(min, n));
};
const fmt = (n) => '$' + Math.round(n).toLocaleString('en-US');

const CloudMigrationEstimatorPage = () => {
    usePageSeo({
        title: 'Cloud Migration Cost Calculator — 3-Year TCO',
        description: 'Free cloud migration cost calculator. Compare on-prem vs cloud monthly cost by servers, users, and storage — plus a one-time migration estimate and 3-year TCO.',
        keywords: 'cloud migration cost estimator, on-prem vs cloud cost, cloud TCO calculator, server migration cost, Azure AWS cost comparison, managed cloud California, BUILDLAB',
    });

    const [servers, setServers] = useState(4);
    const [users, setUsers] = useState(25);
    const [storage, setStorage] = useState(5);

    const r = useMemo(() => {
        const onprem = servers * ONPREM.perServer + storage * ONPREM.perTB + users * ONPREM.perUser + ONPREM.overhead;
        const cloud = servers * CLOUD.perServer + storage * CLOUD.perTB + users * CLOUD.perUser + CLOUD.overhead;
        const monthlyDelta = onprem - cloud; // positive => cloud cheaper
        const migration = servers * MIGRATION_PER_SERVER;
        const onprem3yr = onprem * 36;
        const cloud3yr = cloud * 36 + migration;
        const payback = monthlyDelta > 0 ? migration / monthlyDelta : null; // months
        return { onprem, cloud, monthlyDelta, migration, onprem3yr, cloud3yr, payback, cloudCheaper: monthlyDelta > 0 };
    }, [servers, users, storage]);

    return (
        <Fragment>
            <Header />
            <main id="main-content" className="page_content">
                <PageTitle pageTitle={'Cloud Migration Cost Calculator'} pagesub={'Calculator'} pageTop={'Business IT'} />
                <JsonLd data={webAppSchema({ name: 'Cloud Migration Cost Calculator', description: 'Free cloud migration cost calculator — compare on-prem vs cloud monthly cost and 3-year TCO.', path: '/cloud-migration-estimator' })} />
                <section className="section_space bg-light">
                    <div className="container">
                        <div className="row mb-4">
                            <div className="col-lg-9">
                                <h2 className="mb-3">On-prem vs. cloud — what would you actually pay?</h2>
                                <p>
                                    Compare the running cost of your current on-premises servers against an equivalent managed-cloud
                                    setup, plus a ballpark one-time migration cost and a 3-year total. Use it to frame the business case;
                                    we'll right-size the architecture so you don't over-provision.
                                </p>
                            </div>
                        </div>

                        <div className="row">
                            {/* Inputs */}
                            <div className="col-lg-7 mb-4">
                                <div style={{ background: '#fff', border: '1px solid var(--bs-border-color)', borderRadius: 16, padding: '24px 28px' }}>
                                    <div className="row">
                                        <div className="col-sm-4 mb-3">
                                            <label className="input_title d-block mb-1">Servers / VMs</label>
                                            <input type="number" className="form-control" min="1" max="200" value={servers}
                                                onChange={(e) => setServers(clampInt(e.target.value, 1, 200))} aria-label="Number of servers" />
                                        </div>
                                        <div className="col-sm-4 mb-3">
                                            <label className="input_title d-block mb-1">Users</label>
                                            <input type="number" className="form-control" min="1" max="5000" value={users}
                                                onChange={(e) => setUsers(clampInt(e.target.value, 1, 5000))} aria-label="Number of users" />
                                        </div>
                                        <div className="col-sm-4 mb-3">
                                            <label className="input_title d-block mb-1">Storage (TB)</label>
                                            <input type="number" className="form-control" min="1" max="2000" value={storage}
                                                onChange={(e) => setStorage(clampInt(e.target.value, 1, 2000))} aria-label="Storage in terabytes" />
                                        </div>
                                    </div>
                                    <div className="row mt-2">
                                        <div className="col-md-6 mb-2">
                                            <div style={{ background: 'var(--bs-light)', borderRadius: 12, padding: '16px 18px' }}>
                                                <div style={{ fontSize: '.8rem', color: '#6c757d' }}>On-premises / month</div>
                                                <div style={{ fontSize: '1.5rem', fontWeight: 800 }}>{fmt(r.onprem)}</div>
                                            </div>
                                        </div>
                                        <div className="col-md-6 mb-2">
                                            <div style={{ background: 'var(--bs-light)', borderRadius: 12, padding: '16px 18px' }}>
                                                <div style={{ fontSize: '.8rem', color: '#6c757d' }}>Managed cloud / month</div>
                                                <div style={{ fontSize: '1.5rem', fontWeight: 800 }}>{fmt(r.cloud)}</div>
                                            </div>
                                        </div>
                                    </div>
                                    <p className="small text-muted mb-0 mt-2">
                                        On-prem includes amortized hardware, power/cooling/space, and in-house admin time. Cloud is
                                        managed opex including identity and backup.
                                    </p>
                                </div>
                            </div>

                            {/* Result */}
                            <div className="col-lg-5 mb-4">
                                <div style={{ background: 'var(--bs-dark)', borderRadius: 16, padding: '28px 30px', color: '#fff', height: '100%' }}>
                                    <div style={{ fontSize: '.85rem', opacity: 0.8 }}>{r.cloudCheaper ? 'Estimated monthly savings in the cloud' : 'Estimated monthly difference'}</div>
                                    <div style={{ fontSize: '2rem', fontWeight: 800, lineHeight: 1.1, margin: '4px 0 14px', color: r.cloudCheaper ? '#7CFFB2' : '#FFC9A3' }}>
                                        {r.cloudCheaper ? `${fmt(r.monthlyDelta)}/mo` : `${fmt(Math.abs(r.monthlyDelta))}/mo more`}
                                    </div>
                                    <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: '6px 12px', fontSize: '.88rem' }}>
                                        <span style={{ opacity: 0.8 }}>One-time migration</span><strong style={{ textAlign: 'right' }}>{fmt(r.migration)}</strong>
                                        <span style={{ opacity: 0.8 }}>On-prem 3-yr total</span><strong style={{ textAlign: 'right' }}>{fmt(r.onprem3yr)}</strong>
                                        <span style={{ opacity: 0.8 }}>Cloud 3-yr total</span><strong style={{ textAlign: 'right' }}>{fmt(r.cloud3yr)}</strong>
                                        {r.payback && (
                                            <>
                                                <span style={{ opacity: 0.8 }}>Migration payback</span>
                                                <strong style={{ textAlign: 'right' }}>~{Math.ceil(r.payback)} mo</strong>
                                            </>
                                        )}
                                    </div>
                                    <p style={{ fontSize: '.82rem', opacity: 0.78, marginTop: 12 }}>
                                        Cloud value isn't only cost — it's elasticity, resilience, and security. We model your real
                                        workloads before recommending lift-and-shift vs. re-platforming.
                                    </p>
                                    <Link to="/contact" className="btn btn-primary w-100 mt-2" style={{ display: 'block', textAlign: 'center' }}>
                                        <span className="btn_label" data-text="Plan our cloud migration">Plan our cloud migration</span>
                                    </Link>
                                    <ToolLeadCapture
                                        tool="Cloud Migration Estimator"
                                        summary={`${servers} servers · ${users} users · ${storage} TB — on-prem ${fmt(r.onprem)}/mo vs cloud ${fmt(r.cloud)}/mo (${r.cloudCheaper ? 'saves ' + fmt(r.monthlyDelta) : fmt(Math.abs(r.monthlyDelta)) + ' more'}/mo) · migration ${fmt(r.migration)}${r.payback ? ' · payback ~' + Math.ceil(r.payback) + ' mo' : ''}`}
                                    />
                                </div>
                            </div>
                        </div>

                        <p className="small text-muted mt-3" style={{ maxWidth: 880 }}>
                            Estimates are planning-level ballparks using typical California SMB rates; they are not a quote and exclude
                            licensing, egress, and compliance specifics. Actual cost depends on workload profiles, reserved-instance
                            commitments, and data-transfer patterns. BUILDLAB provides cloud assessments, migration, and managed cloud operations.
                        </p>

                        <p className="small mt-3" style={{ maxWidth: 880 }}>
                            Related tools:{' '}
                            <Link to="/it-cost-calculator">Managed IT cost calculator</Link> ·{' '}
                            <Link to="/it-services">Managed IT services &amp; pricing</Link>
                        </p>
                    </div>
                </section>
                <CtaSection />
            </main>
            <Footer />
            <Scrollbar />
        </Fragment>
    );
};

export default CloudMigrationEstimatorPage;
